import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { LabelInput } from '@/common/components/input/LabelInput';
import { Button } from '@/common/components/button/Button';
import { semanticColors } from '@/common/styles/colorTokens';
import { authService } from '@/services/authService';
import { isValidEmail } from '@/common/utils/textFormat';
import { toastUtils } from '@/common/utils/toastUtils';
import { commonService } from '@/services/commonService';
import { Mail, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { typography } from '@/common/styles/typographyTokens';

interface EmailChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
  onSave: () => void;
}

const EmailChangeModal: React.FC<EmailChangeModalProps> = ({
  isOpen,
  onClose,
  currentEmail,
  onSave,
}) => {
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEmailChecked, setIsEmailChecked] = useState(false);

  // 이메일 입력 시 중복 확인 상태 초기화
  useEffect(() => {
    if (isEmailChecked) {
      setIsEmailChecked(false);
    }
  }, [newEmail]);

  const handleEmailChange = (value: string) => {
    setNewEmail(value);

    if (value && !isValidEmail(value)) {
      setEmailError('올바른 이메일 형식을 입력해주세요.');
    } else if (currentEmail === value) {
      setEmailError('현재 이메일과 동일합니다.');
    } else {
      setEmailError('');
    }
  };

  const checkEmailDuplicate = async () => {
    if (!newEmail || !!emailError || isEmailChecked) return;

    setIsCheckingDuplicate(true);
    try {
      const result = await commonService.checkDuplicateValue('email', newEmail);
      if (result?.exists) {
        setEmailError('이미 사용 중인 이메일입니다.');
        setIsEmailChecked(false);
      } else {
        setEmailError('');
        setIsEmailChecked(true);
        toastUtils.success('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      setEmailError('중복 확인 중 오류가 발생했습니다.');
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleSave = async () => {
    if (!newEmail.trim() || !isEmailChecked) return;
    setIsUpdating(true);
    try {
      const result = await authService.resendVerifyEmail(newEmail);
      if (result.success) {
        toastUtils.success('이메일 변경이 완료되었습니다. 새 이메일에서 인증을 완료해주세요.');
        onSave();
      } else {
        toastUtils.error(result.message || '이메일 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('이메일 변경 실패:', error);
      toastUtils.error('이메일 변경 중 오류가 발생했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <ModalCenter
      isOpen={isOpen}
      onClose={onClose}
      autoClose={false}
      title="이메일 변경"
      maxWidth="520px"
      footer={
        <ModalButtonContainer>
          <Button
            $variant="outline"
            $size="md"
            onClick={onClose}
            disabled={isUpdating}
            style={{ flex: 1 }}
          >
            취소
          </Button>
          <Button
            $variant="fill"
            $size="md"
            onClick={handleSave}
            disabled={isUpdating || !!emailError || !newEmail.trim() || !isEmailChecked}
            style={{ flex: 1 }}
          >
            {isUpdating ? (
              <>
                <Loader2 size={18} className="animate-spin" style={{ marginRight: '8px' }} />
                변경 중...
              </>
            ) : '변경하기'}
          </Button>
        </ModalButtonContainer>
      }
    >
      <ModalContentContainer>
        <InfoBox>
          <Mail size={20} color={semanticColors.text.brand} />
          <p>이메일 주소를 변경하면 <strong>새로운 이메일로 인증 메일이 발송</strong>됩니다. 인증을 완료해야 정상적으로 서비스를 이용하실 수 있습니다.</p>
        </InfoBox>

        <InputSection>
          <LabelInput
            label="현재 이메일 주소"
            value={currentEmail}
            readOnly
            disabled
          />
        </InputSection>

        <InputSection>
          <EmailInputWrapper>
            <div style={{ flex: 1 }}>
              <LabelInput
                label="새 이메일 주소"
                value={newEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="address@example.com"
                error={!!emailError}
                errorMessage={emailError}
                disabled={isUpdating}
              />
            </div>
            <CheckButtonContainer>
              <CheckButton            
                onClick={checkEmailDuplicate}
                disabled={isCheckingDuplicate || isUpdating || !newEmail.trim() || !!emailError || isEmailChecked}
                $isChecked={isEmailChecked}
              >
                {isCheckingDuplicate ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isEmailChecked ? (
                  <CheckCircle2 size={16} />
                ) : '중복확인'}
                <span style={{ marginLeft: '4px' }}>
                  {isCheckingDuplicate ? '확인 중' : isEmailChecked ? '확인 완료' : '중복확인'}
                </span>
              </CheckButton>
            </CheckButtonContainer>
          </EmailInputWrapper>
          {isEmailChecked && !emailError && (
            <SuccessText>
              <CheckCircle2 size={14} />
              사용 가능한 이메일 주소입니다.
            </SuccessText>
          )}
        </InputSection>
      </ModalContentContainer>
    </ModalCenter>
  );
};

const ModalContentContainer = styled.div`
  width: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 1rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
  border: 1px solid ${semanticColors.border.primary};

  p {
    margin: 0;
    ${typography.caption.caption1.default}
    color: ${semanticColors.text.secondary};
    line-height: 1.5;
    
    strong {
      color: ${semanticColors.text.primary};
      font-weight: 600;
    }
  }
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const EmailInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const CheckButtonContainer = styled.div`
  margin-top: 26px;
`;

const CheckButton = styled.button<{ $isChecked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  height: 48px;
  background: ${props => props.$isChecked ? semanticColors.background.success.secondary : semanticColors.background.brand};
  color: ${props => props.$isChecked ? semanticColors.text.onSuccess.primary : semanticColors.text.white};
  border: 1px solid ${props => props.$isChecked ? semanticColors.border.success : 'transparent'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    background: ${props => props.$isChecked ? semanticColors.background.success.secondaryHover : semanticColors.background.brandHover};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SuccessText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.onSuccess.primary};
  margin-top: 4px;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;
`;

export default EmailChangeModal;
